import { c as _c } from "react/compiler-runtime";
import Button from '@precis-digital/kurama/Button';
import Divider from '@precis-digital/kurama/Divider';
import DotsLoader from '@precis-digital/kurama/DotsLoader';
import Grid from '@precis-digital/kurama/Grid';
import SettingsIcon from '@precis-digital/kurama/icons/SettingsIcon';
import Skeleton from '@precis-digital/kurama/Skeleton';
import Typography from '@precis-digital/kurama/Typography';
import { styled } from '@precis-digital/kurama/utils';
import React, { type Dispatch, type SetStateAction, useCallback, useEffect, useRef } from 'react';
import { UnStyledRetainDefaultQueryLink } from 'shared/components/Router';
import { useCurrentClient } from 'shared/context/ClientContext';
import { useTranslation } from 'shared/translations';
import { useMarkNotificationAsRead } from '../../../api';
import { useNotificationPresentationConfig } from './useNotificationPresentationConfig';
import { APP_ROUTES } from 'shared/routes';
import { StyledFlyoutFooter, StyledGraySpan } from '../styles';
import NotificationCard from '../../NotificationCard';
import { UnstyledLink } from 'shared/components/UnstyledLink';
import { useNotifications } from '../../../hooks/useNotifications';
interface NotificationsFlyoutProps {
  handleFlyout: Dispatch<SetStateAction<boolean>>;
  open: boolean;
}
const NotificationsFlyout = t0 => {
  const $ = _c(57);
  const {
    handleFlyout,
    open
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      unreadOnly: false
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    groupedNotifications,
    allNotifications,
    notificationsLoading,
    isProcessingMore,
    hasMoreToDisplay,
    loadMoreDisplayedNotifications,
    refetchNotifications
  } = useNotifications(t1);
  let t2;
  let t3;
  if ($[1] !== open || $[2] !== refetchNotifications) {
    t2 = () => {
      if (open) {
        refetchNotifications();
      }
    };
    t3 = [open, refetchNotifications];
    $[1] = open;
    $[2] = refetchNotifications;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  const {
    t
  } = useTranslation("workspace");
  const notificationPresentationConfig = useNotificationPresentationConfig();
  const {
    currentClient
  } = useCurrentClient();
  const {
    mutate: markAsRead
  } = useMarkNotificationAsRead();
  let t4;
  if ($[5] !== allNotifications || $[6] !== currentClient.id || $[7] !== handleFlyout || $[8] !== markAsRead) {
    t4 = () => {
      const notificationsToMarkAsRead = allNotifications.filter(_temp).map(_temp2);
      markAsRead({
        clientId: currentClient.id,
        userNotificationIds: notificationsToMarkAsRead
      });
      handleFlyout(false);
    };
    $[5] = allNotifications;
    $[6] = currentClient.id;
    $[7] = handleFlyout;
    $[8] = markAsRead;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const handleFlyoutExit = t4;
  const observer = useRef(null);
  let t5;
  if ($[10] !== hasMoreToDisplay || $[11] !== isProcessingMore || $[12] !== loadMoreDisplayedNotifications || $[13] !== notificationsLoading) {
    t5 = node => {
      if (notificationsLoading || isProcessingMore || !hasMoreToDisplay) {
        return;
      }
      if (observer.current !== null) {
        observer.current.disconnect();
      }
      observer.current = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting && hasMoreToDisplay && !isProcessingMore) {
          loadMoreDisplayedNotifications();
        }
      });
      if (node !== null) {
        observer.current.observe(node);
      }
    };
    $[10] = hasMoreToDisplay;
    $[11] = isProcessingMore;
    $[12] = loadMoreDisplayedNotifications;
    $[13] = notificationsLoading;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const lastNotificationElementRef = t5;
  let t6;
  if ($[15] !== t) {
    t6 = t("notificationsFlyout.allNotifications");
    $[15] = t;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== t6) {
    t7 = <Typography variant="h2">{t6}</Typography>;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Divider />;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== groupedNotifications || $[21] !== notificationsLoading || $[22] !== t) {
    t9 = !notificationsLoading && Object.keys(groupedNotifications).length === 0 && <StyledGraySpan><Typography variant="body1">{t("notificationsFlyout.noMoreNotifications")}</Typography></StyledGraySpan>;
    $[20] = groupedNotifications;
    $[21] = notificationsLoading;
    $[22] = t;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== groupedNotifications || $[25] !== handleFlyoutExit || $[26] !== lastNotificationElementRef || $[27] !== notificationPresentationConfig || $[28] !== notificationsLoading) {
    t10 = notificationsLoading && Object.keys(groupedNotifications).length === 0 ? <Grid container={true} gap="16px" style={{
      marginTop: "16px"
    }}><Skeleton height={24} width={96} />{Array(4).fill(0).map(_temp3)}<Skeleton height={24} width={96} />{Array(8).fill(0).map(_temp4)}</Grid> : <>{Object.entries(groupedNotifications).map((t11, dateIndex, dateArray) => {
        const [dateKey, notifications] = t11;
        return <React.Fragment key={dateKey}><Typography variant="body2" style={{
            marginTop: "16px"
          }}><StyledGraySpan><b>{dateKey}</b></StyledGraySpan></Typography><StyledNotificationListWrapper container={true} direction="column">{notifications.map((notification_1, notificationIndex, notificationArray) => {
              const isLastNotification = dateIndex === dateArray.length - 1 && notificationIndex === notificationArray.length - 1;
              return <UnstyledLink key={notification_1.id !== null ? notification_1.id : `notification-${notificationIndex}`} onClick={handleFlyoutExit} href={(() => {
                const href = notificationPresentationConfig[notification_1.source]?.href;
                return typeof href === "function" ? href(notification_1) : href ?? "";
              })()} ref={isLastNotification ? lastNotificationElementRef : null}><NotificationCard title={notification_1.title} description={notification_1.message} type={notification_1.source} date={notification_1.createdAt} isRead={notification_1.isRead} /></UnstyledLink>;
            })}</StyledNotificationListWrapper></React.Fragment>;
      })}</>;
    $[24] = groupedNotifications;
    $[25] = handleFlyoutExit;
    $[26] = lastNotificationElementRef;
    $[27] = notificationPresentationConfig;
    $[28] = notificationsLoading;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== groupedNotifications || $[31] !== isProcessingMore || $[32] !== notificationsLoading) {
    t11 = (notificationsLoading || isProcessingMore) && Object.keys(groupedNotifications).length > 0 && <Grid container={true} justifyContent="center" style={{
      padding: "16px 0"
    }}><DotsLoader /></Grid>;
    $[30] = groupedNotifications;
    $[31] = isProcessingMore;
    $[32] = notificationsLoading;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  if ($[34] !== groupedNotifications || $[35] !== hasMoreToDisplay || $[36] !== notificationsLoading || $[37] !== t) {
    t12 = !hasMoreToDisplay && !notificationsLoading && Object.keys(groupedNotifications).length > 0 && <Grid container={true} justifyContent="center" style={{
      padding: "32px 0px 64px 0px"
    }}><Typography variant="body2" color="secondary">{t("notificationsFlyout.noMoreNotifications")}</Typography></Grid>;
    $[34] = groupedNotifications;
    $[35] = hasMoreToDisplay;
    $[36] = notificationsLoading;
    $[37] = t;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  let t13;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <Divider />;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  let t14;
  if ($[40] !== handleFlyoutExit) {
    t14 = () => {
      handleFlyoutExit();
    };
    $[40] = handleFlyoutExit;
    $[41] = t14;
  } else {
    t14 = $[41];
  }
  let t15;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <SettingsIcon />;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  let t16;
  if ($[43] !== t) {
    t16 = t("notificationsFlyout.editNotificationSettings");
    $[43] = t;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  let t17;
  if ($[45] !== t16) {
    t17 = <Grid container={true} alignItems="center" justifyContent="center" gap="4px">{t15}{t16}</Grid>;
    $[45] = t16;
    $[46] = t17;
  } else {
    t17 = $[46];
  }
  let t18;
  if ($[47] !== t14 || $[48] !== t17) {
    t18 = <StyledFlyoutFooter>{t13}<UnStyledRetainDefaultQueryLink href={APP_ROUTES.workspace.notificationSettingsPage}><Button variant="outlined" fullWidth={true} onClick={t14}>{t17}</Button></UnStyledRetainDefaultQueryLink></StyledFlyoutFooter>;
    $[47] = t14;
    $[48] = t17;
    $[49] = t18;
  } else {
    t18 = $[49];
  }
  let t19;
  if ($[50] !== t10 || $[51] !== t11 || $[52] !== t12 || $[53] !== t18 || $[54] !== t7 || $[55] !== t9) {
    t19 = <StyledNotificationsFlyout>{t7}{t8}{t9}{t10}{t11}{t12}{t18}</StyledNotificationsFlyout>;
    $[50] = t10;
    $[51] = t11;
    $[52] = t12;
    $[53] = t18;
    $[54] = t7;
    $[55] = t9;
    $[56] = t19;
  } else {
    t19 = $[56];
  }
  return t19;
};
const StyledNotificationsFlyout = styled(Grid)(() => ({
  paddingTop: '20px',
  paddingBottom: '48px',
  display: 'flex',
  flexDirection: 'column'
}));
const StyledNotificationListWrapper = styled(Grid)(() => ({
  padding: '16px 0px'
}));
export default NotificationsFlyout;
function _temp(notification) {
  return !notification.isRead;
}
function _temp2(notification_0) {
  return notification_0.id;
}
function _temp3(_, index) {
  return <Skeleton key={index} height={80} />;
}
function _temp4(__0, index_0) {
  return <Skeleton key={index_0} height={80} />;
}